import { Button, Input, useToast } from '@ais-ui/ui-core';
import React, { useEffect, useMemo, useState } from 'react';
import { useSettings } from '../../../Settings/SettingsProvider';
import {
  LegendColor,
  useMapLegendColors,
  useSaveMapLegendsToSettings,
} from '@ais-ui/websockets';
import { LegendItem } from './LegendItem';
import { useReplayContext } from '../../../Replay/ReplayContext';
import { markErrorHandledLocally } from '@ais-ui/custom-hooks';

interface ColorByTargetAgeProps {
  numIntervals: number;
  setNumIntervals: (value: number) => void;
  intervalSize: number;
  setIntervalSize: (value: number) => void;
}

interface UpdateTargetAgeProps {
  numIntervals: number;
  intervalSize: number;
}

export const UpdateTargetAge = ({
  numIntervals,
  intervalSize,
}: UpdateTargetAgeProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { settings } = useSettings();
  const { isPlaying } = useReplayContext();
  const { mutate } = useSaveMapLegendsToSettings(isPlaying);
  const { toast } = useToast();
  const onUpdateTargetAge = () => {
    const unit = 'm'; // m for minutes. Can also be h for hour

    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        legend_age_intervals: numIntervals,
        legend_age_intervalsize: intervalSize,
        legend_age_intervalunit: unit,
      },
    };

    mutate(
      {
        settings: settingsToSave,
      },
      {
        onSuccess: () => {
          toast({
            className: 'apply-color-by-success-toast',
            title: 'Success!',
            description: 'Successfully updated target age legend',
          });
          setIsSubmitting(false);
        },
        onError: (error) => {
          markErrorHandledLocally(error);
          toast({
            className: 'apply-color-by-error-toast',
            title: 'Error!',
            description: 'Failed to update target age legend',
            variant: 'destructive',
          });
          setIsSubmitting(false);
        },
      },
    );
  };

  return (
    <Button variant="ghost" onClick={onUpdateTargetAge} disabled={isSubmitting}>
      Update
    </Button>
  );
};

const TargetAgeParameters = ({
  numIntervals,
  setNumIntervals,
  intervalSize,
  setIntervalSize,
}: ColorByTargetAgeProps) => {
  return (
    <div className="flex space-x-8 mx-2">
      <div className="flex space-x-4 items-center justify-between">
        <label htmlFor="numIntervals" className="text-sm flex-shrink-0">
          Number of intervals
        </label>
        <Input
          id="numIntervals"
          className="w-20"
          type="number"
          min={2}
          max={20}
          value={numIntervals}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            if (!isNaN(value)) setNumIntervals(value);
          }}
        />
      </div>

      <div className="flex space-x-4 items-center justify-between">
        <div>
          <label htmlFor="intervalSize" className="text-sm flex-shrink-0">
            Interval size
          </label>
          <div className="text-sm text-muted-foreground">(in minutes)</div>
        </div>
        <Input
          id="intervalSize"
          className="w-20"
          type="number"
          min={1}
          max={59}
          value={intervalSize}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            if (!isNaN(value)) setIntervalSize(value);
          }}
        />
      </div>
    </div>
  );
};

export const ColorByTargetAge = ({
  numIntervals,
  setNumIntervals,
  intervalSize,
  setIntervalSize,
}: ColorByTargetAgeProps) => {
  const [displayLegendItems, setDisplayLegendItems] = useState<LegendColor[]>(
    [],
  );

  const { data: legendColors } = useMapLegendColors();

  const validColors = useMemo(() => {
    return legendColors?.filter((item) => Boolean(item.color)) || [];
  }, [legendColors]);

  useEffect(() => {
    setDisplayLegendItems(validColors);
  }, [validColors]);

  return (
    <div className="space-y-1">
      <TargetAgeParameters
        numIntervals={numIntervals}
        setNumIntervals={setNumIntervals}
        intervalSize={intervalSize}
        setIntervalSize={setIntervalSize}
      ></TargetAgeParameters>
      <div className="p-4 grid grid-cols-3 gap-4 bg-background">
        {displayLegendItems.map((item) => {
          return (
            <LegendItem
              key={item.name}
              category={item.name}
              color={item.color}
            />
          );
        })}
      </div>
    </div>
  );
};
